import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import ErrorBoundary from './components/ErrorBoundary';
import { MainLayout } from './components/Layout/Layout';
import AccountsPage from './pages/AccountsPage';
import CalendarPage from './pages/CalendarPage';
import CreatePostPage from './pages/CreatePostPage';
import DashboardPage from './pages/DashboardPage';
import EditorPage from './pages/EditorPage';
import AdminPage from './pages/AdminPage';
import AudioLibraryPage from './pages/AudioLibraryPage';
import HashtagsPage from './pages/HashtagsPage';
import LoginPage from './pages/LoginPage';
import WizardPage from './pages/WizardPage';
import apiClient from './services/api';
import { User } from './types/api';
import './App.css';
import './components/ErrorBoundary.css';

interface MeResponse {
  authenticated: boolean;
  user: User | null;
}

function App(): ReactElement {
  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const data = await apiClient.get<MeResponse>('/accounts/me/');
      setUser(data.authenticated ? data.user : null);
    } catch {
      setUser(null);
    } finally {
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  async function handleLogout(): Promise<void> {
    try { await apiClient.post('/accounts/logout/', {}); } catch (_) { /* ignore */ }
    setUser(null);
  }

  if (!authChecked) {
    return <div className="app-boot">Loading…</div>;
  }

  if (!user) {
    return <LoginPage onLoggedIn={(u) => setUser(u)} />;
  }

  return (
    <BrowserRouter>
      <MainLayout user={user} onLogout={handleLogout}>
        <div className="tab-content">
          <AppRoutes user={user} />
        </div>
      </MainLayout>
    </BrowserRouter>
  );
}

function AppRoutes({ user }: { user: User | null }): ReactElement {
  const navigate = useNavigate();
  return (
    <Routes>
      {/* The guided wizard is now the front door of the app. / sends
          the user there; if there's an in-progress wizard in
          localStorage we resume into it, otherwise we mint a fresh
          VideoProject and land on the upload step. */}
      <Route path="/" element={<Navigate to="/start" replace />} />
      <Route path="/start" element={<WizardPage />} />
      <Route path="/start/:projectId/:step" element={<WizardPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/calendar" element={<CalendarPage />} />
      <Route path="/create" element={<CreatePostPageRoute />} />
      <Route path="/editor" element={
        <ErrorBoundary label="Video editor crashed">
          <EditorPage onUseInPost={(postId) => navigate(`/create?postId=${postId}`)} />
        </ErrorBoundary>
      } />
      <Route path="/editor/:projectId" element={
        <ErrorBoundary label="Video editor crashed">
          <EditorPage onUseInPost={(postId) => navigate(`/create?postId=${postId}`)} />
        </ErrorBoundary>
      } />
      <Route path="/accounts" element={<AccountsPage />} />
      <Route path="/hashtags" element={<HashtagsPage />} />
      <Route path="/audio" element={<AudioLibraryPage />} />
      {user?.is_staff && <Route path="/admin" element={<AdminPage />} />}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function CreatePostPageRoute(): ReactElement {
  const navigate = useNavigate();
  return <CreatePostPage onCreated={() => navigate('/')} />;
}

export default App;
